import React, { useState } from 'react';
import { useData } from '../contexts/DataContext';
import { useT } from '../lib/i18n';
import { buildSignUrl, shareOrCopyUrl } from '../lib/shareLink';
import { Send, Loader2, X } from 'lucide-react';

const DAYS = 7 * 86400000;

export function ReminderBanner({ submission, onReminded }) {
  const { ensureSignToken, markReminded } = useData();
  const { lang } = useT();
  const [sending, setSending] = useState(false);

  if (!submission) return null;
  if (submission.signed_at) return null;
  if (submission.status !== 'pending') return null;
  if (!submission.sign_token) return null;

  const ref = submission.last_reminded_at || submission.updated_at || submission.created_at;
  const last = ref ? new Date(ref).getTime() : 0;
  if (!last) return null;
  const daysSince = Math.floor((Date.now() - last) / 86400000);
  if (daysSince < 7) return null;

  const onResend = async () => {
    setSending(true);
    try {
      const updated = await ensureSignToken(submission.id);
      const token = updated?.sign_token || submission.sign_token;
      const url = buildSignUrl(token);
      const text =
        lang === 'fr'
          ? `Bonjour ${submission.client_name},\n\nUn rappel amical : votre soumission ${submission.submission_number} est en attente de signature.\n\nSignez ici : ${url}`
          : `Hi ${submission.client_name},\n\nFriendly reminder: your quote ${submission.submission_number} is awaiting signature.\n\nSign here: ${url}`;
      await shareOrCopyUrl({
        title: `Soumission ${submission.submission_number}`,
        text,
        url,
        lang,
      });
      await markReminded(submission.id);
      if (onReminded) onReminded();
    } finally {
      setSending(false);
    }
  };

  const dismiss = async () => {
    await markReminded(submission.id);
    if (onReminded) onReminded();
  };

  return (
    <div data-testid="reminder-banner" className="card border-2 border-amber-300 bg-amber-50 p-4 mb-4">
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <div className="font-heading font-black text-amber-900">
            {lang === 'fr' ? `Sans réponse depuis ${daysSince} jours` : `${daysSince} days without reply`}
          </div>
          <div className="text-xs text-amber-800 mt-1">
            {lang === 'fr'
              ? 'Votre client n\u2019a pas encore signé. Voulez-vous lui renvoyer le lien ?'
              : 'Your client hasn\u2019t signed yet. Want to resend the link?'}
          </div>
          <div className="flex gap-2 mt-3">
            <button
              data-testid="reminder-resend"
              onClick={onResend}
              disabled={sending}
              className="btn-primary gap-2 !min-h-[40px] !text-sm flex-1"
            >
              {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />}
              {lang === 'fr' ? 'Renvoyer le lien' : 'Resend link'}
            </button>
            <button
              data-testid="reminder-dismiss"
              onClick={dismiss}
              className="btn-outline gap-2 !min-h-[40px] !text-sm !text-amber-900 !border-amber-300"
            >
              <X size={14} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
